import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaEye, FaArrowLeft } from 'react-icons/fa';

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current === false) {
      const fetchBlog = async () => {
        try {
          const response = await fetch(`https://my-portfoli-backed.onrender.com/api/blogs/${id}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'فشل جلب البيانات');
          }
          
          const data = await response.json();
          setBlog(data.data.blog);
        } catch (err) {
          console.error('Error details:', err);
          setError(`حدث خطأ: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };

      fetchBlog();

      return () => {
        effectRan.current = true;
      }
    }
  }, [id]);

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      color: '#333333',
      minHeight: '100vh',
      padding: '4rem 2rem',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '3rem',
      borderBottom: '2px solid #000000',
      paddingBottom: '1.5rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.5rem',
      lineHeight: '1.3'
    },
    metaContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    blogMeta: {
      display: 'flex',
      alignItems: 'center',
      color: '#666666',
      fontSize: '0.9rem',
      gap: '1.5rem',
      flexWrap: 'wrap'
    },
    blogDate: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem'
    },
    blogCategory: {
      backgroundColor: '#f0f0f0',
      padding: '0.3rem 0.8rem',
      borderRadius: '4px'
    },
    viewsCount: {
      display: 'flex',
      alignItems: 'center',
      color: '#666666',
      fontSize: '0.9rem',
      gap: '0.3rem'
    },
    tagContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '2rem'
    },
    tag: {
      backgroundColor: '#f5f5f5',
      padding: '0.3rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      color: '#555555'
    },
    blogContent: {
      color: '#333333',
      fontSize: '1.1rem',
      lineHeight: '1.8',
      marginBottom: '3rem'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      color: '#000000',
      fontWeight: '500',
      textDecoration: 'none',
      borderBottom: '2px solid #000000',
      paddingBottom: '0.3rem',
      transition: 'color 0.3s ease',
      gap: '0.5rem',
      ':hover': {
        color: '#333333'
      }
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      fontSize: '1.2rem'
    },
    error: {
      textAlign: 'center',
      padding: '2rem',
      fontSize: '1.2rem',
      color: '#ff0000'
    }
  };

  if (loading) {
    return <div style={styles.loading}>جاري تحميل المدونة...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h3 style={{ color: '#ff0000' }}>حدث خطأ</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1rem',
            background: '#000',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            margin: '0.5rem'
          }}
        >
          المحاولة مرة أخرى
        </button>
        <Link 
          to="/blogs" 
          style={{
            display: 'inline-block',
            margin: '0.5rem',
            color: '#000',
            textDecoration: 'underline'
          }}
        >
          العودة إلى المدونات
        </Link>
      </div>
    );
  }

  if (!blog) {
    return <div style={styles.error}>لم يتم العثور على المدونة</div>;
  }

  // تحويل تاريخ النشر إلى تنسيق مقروء
  const publishedDate = new Date(blog.publishedAt).toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <header style={styles.header}>
          <h1 style={styles.title}>
            {blog.title}
          </h1>
          
          <div style={styles.metaContainer}>
            <div style={styles.blogMeta}>
              <span style={styles.blogDate}>
                <FaCalendarAlt size={14} />
                {publishedDate}
              </span>
              <span style={styles.blogDate}>
                <FaClock size={14} />
                {blog.readingTime} دقائق قراءة
              </span>
              <span style={styles.blogCategory}>{blog.type}</span>
            </div>
            
            <div style={styles.viewsCount}>
              <FaEye size={14} />
              {blog.views} مشاهدة
            </div>
          </div>
          
          {blog.technologies && blog.technologies.length > 0 && (
            <div style={styles.tagContainer}>
              {blog.technologies.map((tech, index) => (
                <span key={index} style={styles.tag}>{tech}</span>
              ))}
            </div>
          )}
        </header>
        
        <article style={styles.blogContent}>
          {blog.content.split('\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </article>
        
        <Link to="/blogs" style={styles.backButton}>
          <FaArrowLeft size={14} />
          العودة إلى المدونات
        </Link>
      </div>
    </div>
  );
};

export default BlogDetails;